/**
 * CoinCentralBank — admin-only panel showing the platform's coin
 * treasury. Polls `/api/admin/central-bank`, renders:
 *   • Big balance number + lifetime credits/debits
 *   • Breakdown by sink source (Shop purchase, Content unlock, etc.)
 *   • Recent ledger feed
 *   • Disburse form (admin-only writes; co-admins see a read-only view)
 *
 * Only mounted in AdminDashboard — visibility is enforced server-side
 * AND client-side via the viewerRole gate at the parent.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Landmark, RefreshCw, TrendingUp, TrendingDown, Loader2, Send,
  Activity,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (n) => Number(n || 0).toLocaleString();

const CoinCentralBank = ({ canDisburse = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [tEmail, setTEmail] = useState('');
  const [tAmount, setTAmount] = useState(100);
  const [tNote, setTNote] = useState('');
  const [dAmount, setDAmount] = useState(10000);
  const [dNote, setDNote] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/central-bank`);
      if (!r.ok) {
        if (r.status === 403) throw new Error('Admin access required');
        throw new Error(`HTTP ${r.status}`);
      }
      const d = await r.json();
      setData(d);
    } catch (e) {
      setErr(e.message || 'Failed to load central bank');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDisburse = async () => {
    if (!tEmail.trim()) { toast.error('Enter a recipient email'); return; }
    if (!tAmount || tAmount < 1) { toast.error('Amount must be ≥ 1'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/central-bank/disburse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_email: tEmail.trim(),
          amount: Number(tAmount),
          note: tNote.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Disburse failed');
      toast.success(`Disbursed ${fmt(tAmount)} coins · bank balance: ${fmt(d.new_bank_balance)}`);
      setDisburseOpen(false);
      setTEmail(''); setTAmount(100); setTNote('');
      refresh();
    } catch (e) {
      toast.error(e.message || 'Disburse failed');
    } finally {
      setBusy(false);
    }
  };

  // Admin deposit — adds platform reserve coins to the treasury.
  // Surfaces in the ledger under source `admin_deposit` so auditors can
  // distinguish ops top-ups from organic sink credits (shop, perks…).
  const handleDeposit = async () => {
    if (!dAmount || dAmount < 1) { toast.error('Amount must be ≥ 1'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/central-bank/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Number(dAmount),
          note: dNote.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Deposit failed');
      toast.success(`Deposited ${fmt(dAmount)} coins · bank balance: ${fmt(d.new_bank_balance)}`);
      setDepositOpen(false);
      setDAmount(10000); setDNote('');
      refresh();
    } catch (e) {
      toast.error(e.message || 'Deposit failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-slate-900/50 p-6 flex items-center gap-3 text-slate-400" data-testid="central-bank-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading Central Bank…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="rounded-xl border border-rose-500/40 bg-rose-950/30 p-4 text-rose-200 text-sm" data-testid="central-bank-error">
        Couldn&apos;t load the Central Bank: {err || 'unknown error'}
      </div>
    );
  }

  return (
    <section className="space-y-4" data-testid="coin-central-bank">
      <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-slate-950 p-5 shadow-2xl shadow-amber-900/20">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg shadow-amber-900/40">
              <Landmark className="w-6 h-6 text-slate-950" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Admin only · platform treasury</p>
              <h2 className="text-xl sm:text-2xl font-black text-white">Coin Central Bank</h2>
              <p className="text-xs text-slate-300/80">
                Sink for every shop purchase, content unlock, and perk spend that doesn&apos;t flow to another user.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="text-amber-300 hover:text-amber-200 p-2 rounded-md hover:bg-amber-500/10 transition"
            data-testid="central-bank-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-amber-500/30 bg-slate-950/40 p-4" data-testid="central-bank-balance">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Treasury balance</p>
            <p className="text-3xl sm:text-4xl font-black text-amber-100 tabular-nums mt-1">
              {fmt(data.balance)}
            </p>
            <p className="text-[11px] text-slate-400 mt-1">coins</p>
          </div>
          <div className="rounded-xl border border-emerald-500/30 bg-slate-950/40 p-4">
            <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 inline-flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Lifetime credits
            </p>
            <p className="text-2xl font-black text-emerald-200 tabular-nums mt-1">
              {fmt(data.lifetime_credits)}
            </p>
          </div>
          <div className="rounded-xl border border-rose-500/30 bg-slate-950/40 p-4">
            <p className="text-[10px] uppercase tracking-widest font-bold text-rose-300 inline-flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Lifetime debits
            </p>
            <p className="text-2xl font-black text-rose-200 tabular-nums mt-1">
              {fmt(data.lifetime_debits)}
            </p>
          </div>
        </div>

        {canDisburse && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { setDisburseOpen((o) => !o); setDepositOpen(false); }}
                className="inline-flex items-center gap-2 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                data-testid="central-bank-disburse-toggle"
              >
                <Send className="w-3.5 h-3.5" />
                {disburseOpen ? 'Close disburse panel' : 'Disburse to user'}
              </button>
              <button
                type="button"
                onClick={() => { setDepositOpen((o) => !o); setDisburseOpen(false); }}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                data-testid="central-bank-deposit-toggle"
              >
                <TrendingUp className="w-3.5 h-3.5" />
                {depositOpen ? 'Close deposit panel' : 'Deposit to treasury'}
              </button>
            </div>
            {disburseOpen && (
              <div className="rounded-xl border border-amber-500/40 bg-slate-950/60 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="central-bank-disburse-panel">
                <input
                  type="email"
                  value={tEmail}
                  onChange={(e) => setTEmail(e.target.value)}
                  placeholder="recipient@email"
                  className="rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                  data-testid="central-bank-target-email"
                />
                <input
                  type="number"
                  min={1}
                  max={1000000}
                  value={tAmount}
                  onChange={(e) => setTAmount(Math.max(1, Math.min(1_000_000, Number(e.target.value) || 1)))}
                  className="rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                  data-testid="central-bank-target-amount"
                />
                <button
                  type="button"
                  onClick={handleDisburse}
                  disabled={busy}
                  className="rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-bold inline-flex items-center justify-center gap-1.5"
                  data-testid="central-bank-disburse-confirm"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Disburse
                </button>
                <input
                  type="text"
                  value={tNote}
                  onChange={(e) => setTNote(e.target.value.slice(0, 240))}
                  placeholder="Note (optional, audited)…"
                  className="sm:col-span-3 rounded-md bg-slate-900 border border-slate-700 text-white text-xs px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                  data-testid="central-bank-note"
                />
              </div>
            )}
            {depositOpen && (
              <div className="rounded-xl border border-emerald-500/40 bg-slate-950/60 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="central-bank-deposit-panel">
                <input
                  type="number"
                  min={1}
                  max={1_000_000_000}
                  value={dAmount}
                  onChange={(e) => setDAmount(Math.max(1, Math.min(1_000_000_000, Number(e.target.value) || 1)))}
                  placeholder="Amount of coins"
                  className="sm:col-span-2 rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  data-testid="central-bank-deposit-amount"
                />
                <button
                  type="button"
                  onClick={handleDeposit}
                  disabled={busy}
                  className="rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-bold inline-flex items-center justify-center gap-1.5"
                  data-testid="central-bank-deposit-confirm"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
                  Deposit
                </button>
                <input
                  type="text"
                  value={dNote}
                  onChange={(e) => setDNote(e.target.value.slice(0, 240))}
                  placeholder="Note (optional, audited)…"
                  className="sm:col-span-3 rounded-md bg-slate-900 border border-slate-700 text-white text-xs px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  data-testid="central-bank-deposit-note"
                />
                <p className="sm:col-span-3 text-[10px] text-emerald-200/70">
                  Top-ups land instantly in the treasury and are tagged
                  <code className="px-1 mx-0.5 rounded bg-slate-900/80">admin_deposit</code> on the ledger.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* By-source breakdown */}
      <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Sinks (by source)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(data.by_source || []).map((s) => (
            <div
              key={s.source}
              className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-2.5 flex items-center justify-between"
              data-testid={`central-bank-source-${s.source}`}
            >
              <div>
                <p className="text-xs font-bold text-white">{s.label}</p>
                <p className="text-[10px] text-slate-400">{fmt(s.count)} txn · net {fmt(s.net)}</p>
              </div>
              <div className="text-right">
                <p className="text-emerald-300 text-xs tabular-nums">+{fmt(s.credits)}</p>
                {s.debits > 0 && <p className="text-rose-300 text-xs tabular-nums">−{fmt(s.debits)}</p>}
              </div>
            </div>
          ))}
          {(data.by_source || []).length === 0 && (
            <p className="text-xs text-slate-500 italic">No flows yet — the bank just opened its doors.</p>
          )}
        </div>
      </div>

      {/* Ledger */}
      <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2 inline-flex items-center gap-1">
          <Activity className="w-3 h-3" /> Recent ledger
        </p>
        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="central-bank-ledger">
          {(data.recent_ledger || []).slice(0, 40).map((row, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-xs border-b border-slate-800/60 last:border-0 py-1.5">
              <div className="min-w-0 flex-1">
                <p className="text-slate-200 font-semibold truncate">{row.label || row.source}</p>
                <p className="text-[10px] text-slate-500 truncate">
                  {new Date(row.created_at).toLocaleString()}
                  {row.user_id && ` · ${row.user_id}`}
                </p>
              </div>
              <p
                className={`tabular-nums font-bold ${row.direction === 'credit' ? 'text-emerald-300' : 'text-rose-300'}`}
              >
                {row.direction === 'credit' ? '+' : '−'}{fmt(row.amount)}
              </p>
            </div>
          ))}
          {(data.recent_ledger || []).length === 0 && (
            <p className="text-xs text-slate-500 italic">No transactions yet.</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default CoinCentralBank;
